import type { ComponentProps } from "react";
import * as LabelPrimitive from "@radix-ui/react-label";

import { cn } from "../../lib/utils";

export interface LabelProps extends ComponentProps<typeof LabelPrimitive.Root> {
  /** Appends a decorative asterisk. Mark the associated control itself with `required`/`aria-required`. */
  required?: boolean;
}

/**
 * A form-control label built on Radix. Dims and disables pointer events when it follows a disabled
 * `peer` control or sits inside a `group` with `data-disabled="true"`.
 *
 * @example
 * ```tsx
 * <Label htmlFor="email" required>Email</Label>
 * <Input id="email" required />
 * ```
 */
export const Label = ({ className, children, required, ...props }: LabelProps) => (
  <LabelPrimitive.Root
    data-slot="label"
    className={cn(
      "flex items-center gap-2 text-sm leading-none font-medium select-none",
      "group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50",
      "peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
      className
    )}
    {...props}
  >
    {required ? (
      // The marker shares the text's element so it wraps with the last word instead of being laid
      // out as a separate flex item beside the whole text block.
      <span className="min-w-0">
        {children}
        <span aria-hidden className="text-destructive">
          &#8202;*
        </span>
      </span>
    ) : (
      children
    )}
  </LabelPrimitive.Root>
);
